/**
 * Abort Batch Modal Component (Issue #2677)
 * Dialog for aborting a running batch with a required reason
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { Batch } from '../../../models/batch.model'
import { AbortReason, AbortReasonGroup } from '../../../models/machine-event.model'
import { OEEDataService } from '../../../services/oee-data.service'

export interface AbortBatchModalData {
    batch: Batch;
    equipmentName: string;
    equipmentId: string;
    siteId?: string;
}

export interface AbortBatchModalResult {
    aborted: boolean;
    batch_id?: string;
    abort_reason_id?: string;
    end_ts?: string;
}

@Component({
    standalone: false,
    selector: 'app-abort-batch-modal',
    templateUrl: './abort-batch-modal.component.html',
    styleUrls: ['./abort-batch-modal.component.scss']
})
export class AbortBatchModalComponent implements OnInit, OnDestroy {

    private destroy$ = new Subject<void>();

    // Abort reason groups with nested reasons
    abortReasonGroups: AbortReasonGroup[] = [];
    loadingReasons = true;

    // Selected reason
    selectedReasonId: string = '';
    selectedReason: AbortReason | null = null;

    // Submitting state
    submitting = false;

    constructor(
        public dialogRef: MatDialogRef<AbortBatchModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AbortBatchModalData,
        private oeeDataService: OEEDataService,
        private snackBar: MatSnackBar
    ) { }

    ngOnInit(): void {
        this.loadAbortReasons();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Load abort reason groups with nested reasons
     */
    loadAbortReasons(): void {
        this.loadingReasons = true;
        this.oeeDataService.getAbortReasonGroups(this.data.siteId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (groups) => {
                    this.abortReasonGroups = groups;
                    this.loadingReasons = false;
                },
                error: (error) => {
                    console.error('Error loading abort reasons:', error);
                    this.loadingReasons = false;
                    this.snackBar.open('Failed to load abort reasons', 'Dismiss', { duration: 3000 });
                }
            });
    }

    /**
     * Select an abort reason
     */
    selectReason(reason: AbortReason): void {
        this.selectedReasonId = reason.id;
        this.selectedReason = reason;
    }

    /**
     * Check if a reason is selected
     */
    isReasonSelected(reason: AbortReason): boolean {
        return this.selectedReasonId === reason.id;
    }

    /**
     * Check if form is valid
     */
    isValid(): boolean {
        return !!this.selectedReasonId;
    }

    /**
     * Get all reasons from all groups as a flat list
     */
    getAllReasons(): AbortReason[] {
        return ([] as AbortReason[]).concat(...this.abortReasonGroups.map(g => g.reasons || []));
    }

    /**
     * Cancel and close dialog
     */
    onCancel(): void {
        this.dialogRef.close({ aborted: false });
    }

    /**
     * Submit abort request
     */
    onAbort(): void {
        if (!this.isValid()) return;

        this.submitting = true;
        this.oeeDataService.abortBatch({
            batch_id: this.data.batch.batch_id,
            abort_reason_id: this.selectedReasonId
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.submitting = false;
                    this.snackBar.open(
                        `Batch ${result.batch_number} aborted successfully`,
                        'Dismiss',
                        { duration: 3000 }
                    );
                    this.dialogRef.close({
                        aborted: true,
                        batch_id: result.batch_id,
                        abort_reason_id: result.abort_reason_id,
                        end_ts: result.end_ts
                    });
                },
                error: (error) => {
                    this.submitting = false;
                    console.error('Error aborting batch:', error);
                    const message = error.error?.error || 'Failed to abort job';
                    this.snackBar.open(message, 'Dismiss', { duration: 5000 });
                }
            });
    }
}
